import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

const ORIGINAL_TEXT_KEY = 'textOriginal';
const SWAP_TEXT_KEY = 'textSwap';

const WysiwygContent = ({ html, className }) => {
  const containerRef = useRef();
  const keyRef = useRef(0);

  const toggleCollapse = useCallback((element) => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const collapseSelector =
      element.getAttribute('data-target') || element.getAttribute('href');
    const collapseElement = container.querySelector(collapseSelector);
    if (collapseElement) {
      collapseElement.classList.toggle('show');

      // Save original text, if not set.
      if (!element.dataset[ORIGINAL_TEXT_KEY]) {
        element.dataset[ORIGINAL_TEXT_KEY] = element.innerHTML;
      }

      if (!element.dataset[SWAP_TEXT_KEY]) {
        element.dataset[SWAP_TEXT_KEY] = element.dataset[ORIGINAL_TEXT_KEY];
      }

      element.innerHTML =
        element.innerHTML === element.dataset[ORIGINAL_TEXT_KEY]
          ? element.dataset[SWAP_TEXT_KEY]
          : element.dataset[ORIGINAL_TEXT_KEY];
    }
  }, []);

  const handleClick = useCallback(
    (e) => {
      // We need to support some old Bootstrap "data-toggle" attributes.
      if (e.target.dataset && e.target.dataset.toggle) {
        e.preventDefault();
        switch (e.target.dataset.toggle) {
          case 'collapse':
            toggleCollapse(e.target);
            break;
          default:
        }
      }
    },
    [toggleCollapse]
  );

  const getElementAttributes = useCallback((element) => {
    const attributes = { key: keyRef.current++ };
    Array.from(element.attributes).forEach((attr) => {
      if (attr.name === 'id') {
        return;
      }
      attributes[attr.name] = attr.value;
    });

    return attributes;
  }, []);

  const { parsedScripts, sanitizedHtml } = useMemo(() => {
    // Bail if we are in SSR.
    if (typeof document === 'undefined' || window.isSSR) {
      return { parsedScripts: null, sanitizedHtml: html };
    }

    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = html;

    const scriptElements = Array.from(doc.getElementsByTagName('script'));
    const parsedScriptsResult = scriptElements.map((el) => {
      const isLdJson = el.getAttribute('type') === 'application/ld+json';
      const attributes = getElementAttributes(el);

      return isLdJson ? (
        <script {...attributes}>{el.textContent}</script>
      ) : (
        <script defer {...attributes}>
          {el.textContent}
        </script>
      );
    });

    scriptElements.forEach((el) => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });

    return {
      parsedScripts: parsedScriptsResult,
      sanitizedHtml: doc.body.innerHTML,
    };
  }, [getElementAttributes, html]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    container.addEventListener('click', handleClick, true);
    return () => {
      container.removeEventListener('click', handleClick, true);
    };
  }, [handleClick]);

  return (
    <>
      <Helmet>{parsedScripts}</Helmet>
      <div
          className={classNames("WysiwygContent", className)}
        ref={containerRef}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </>
  );
};

WysiwygContent.propTypes = {
  html: PropTypes.string,
  className: PropTypes.string
};

export default WysiwygContent;
